import api from '../api/axios';

// A child's EHCP outcome target (ai.ehcp_target).
export interface EhcpTarget {
  id: number;
  child_id: number;
  target_text: string;
  area: string | null;        // optional EHCP domain slug (e.g. 'semh')
  is_active: boolean;
  sort_order: number;
  has_embedding: boolean;     // false if the embedding hasn't been generated yet
  updated_at: string;
}

export interface GapEvidence {
  post_id: number;
  post_date: string;
  score: number;              // cosine similarity 0..1
  snippet: string;            // desanitised published_text excerpt
}

export interface GapTrendPoint {
  month: string;              // YYYY-MM
  best: number;               // best similarity that month
}

export interface GapTargetResult {
  id: number;
  target_text: string;
  area: string | null;
  max_score: number | null;
  strong_count: number;       // posts at/above threshold
  posts_considered: number;
  gap: boolean;               // true when nothing matched well
  trend: GapTrendPoint[];
  evidence: GapEvidence[];
}

export interface GapAnalysis {
  threshold: number;
  targets: GapTargetResult[];
}

export interface CreateTargetInput {
  childId: number;
  target_text: string;
  area?: string | null;
  sort_order?: number;
}

export interface UpdateTargetInput {
  target_text?: string;
  area?: string | null;
  is_active?: boolean;
  sort_order?: number;
}

/**
 * EHCP targets + the vector gap-analysis that matches them against a child's
 * published-post embeddings (Insights). Reporting/Admin manage; Admin/Editor/
 * Reporting read. Backed by /posts/insights/* in tdp-api.
 */
export class EhcpTargetService {
  static async list(childId: number): Promise<EhcpTarget[]> {
    const res = await api.get('/posts/insights/targets', { params: { childId } });
    return res.data.targets ?? [];
  }

  static async create(input: CreateTargetInput): Promise<EhcpTarget> {
    const res = await api.post('/posts/insights/targets', input);
    return res.data;
  }

  static async update(id: number, input: UpdateTargetInput): Promise<EhcpTarget> {
    const res = await api.put(`/posts/insights/targets/${id}`, input);
    return res.data;
  }

  static async remove(id: number): Promise<void> {
    await api.delete(`/posts/insights/targets/${id}`);
  }

  static async gapAnalysis(
    childId: number,
    from: string,
    to: string,
    opts: { threshold?: number; topN?: number } = {},
  ): Promise<GapAnalysis> {
    const params: Record<string, string | number> = { childId, from, to };
    if (opts.threshold != null) params.threshold = opts.threshold;
    if (opts.topN != null) params.topN = opts.topN;
    const res = await api.get('/posts/insights/gap-analysis', { params });
    return res.data;
  }
}
